import { useState, useEffect, useCallback, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { buildComparePath, assessmentApiUrl, assessmentDetailPath } from '../utils/adminLinks'

// Custom debounce hook following guide pattern
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

// Memoized SearchInput component - defined outside to prevent recreation
// Improved with better accessibility and UX
const SearchInput = memo(({ value, onChange, placeholder }) => {
  return (
    <div className="relative">
      <input
        type="search"
        value={value}
        onChange={onChange}
        className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
        placeholder={placeholder}
        aria-label="Champ de recherche"
        autoComplete="off"
      />
      <svg
        className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
    </div>
  )
})

SearchInput.displayName = 'SearchInput'

// Memoized ClientIdInput component - defined outside to prevent recreation
const ClientIdInput = memo(({ value, onChange, placeholder }) => {
  return (
    <input
      type="text"
      value={value}
      onChange={onChange}
      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
      placeholder={placeholder}
    />
  )
})

ClientIdInput.displayName = 'ClientIdInput'

const AssessmentsList = () => {
  const navigate = useNavigate()
  const [assessments, setAssessments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedIds, setSelectedIds] = useState([])
  const [filters, setFilters] = useState({
    search: '',
    company: '',
    clientId: '',
    dateFrom: '',
    dateTo: '',
    scoreMin: '',
    scoreMax: '',
    includeArchived: false
  })
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  })
  const [companies, setCompanies] = useState([])
  
  // Local input states for debouncing (separate from filters to prevent focus loss)
  const [searchInput, setSearchInput] = useState('')
  const [clientIdInput, setClientIdInput] = useState('')

  // Debounce inputs with 400ms delay (optimal for search APIs)
  const debouncedSearch = useDebounce(searchInput, 400)
  const debouncedClientId = useDebounce(clientIdInput, 400)

  // Memoized fetchAssessments with useCallback
  const fetchAssessments = useCallback(async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('adminToken')
      
      const params = new URLSearchParams()
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          // Handle boolean values specially
          if (typeof value === 'boolean') {
            params.append(key, value.toString())
          } else {
            params.append(key, value)
          }
        }
      })
      params.append('page', pagination.page)
      params.append('limit', pagination.limit)

      const response = await fetch(`/api/admin/assessments?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setAssessments(data.data.assessments)
        setPagination(prev => ({
          ...prev,
          total: data.data.pagination.total,
          pages: data.data.pagination.pages
        }))
      } else {
        throw new Error('Erreur lors du chargement des évaluations')
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [filters, pagination.page, pagination.limit])

  // Memoized fetchCompanies with useCallback
  const fetchCompanies = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken')
      const response = await fetch('/api/admin/companies', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setCompanies(data.data)
      }
    } catch (err) {
      console.error('Error fetching companies:', err)
    }
  }, [])

  // Update filters when debounced values change
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      search: debouncedSearch,
      clientId: debouncedClientId
    }))
    setPagination(prev => ({ ...prev, page: 1 }))
  }, [debouncedSearch, debouncedClientId])

  // Fetch assessments when filters or pagination change
  useEffect(() => {
    fetchAssessments()
  }, [fetchAssessments])

  // Fetch companies on mount
  useEffect(() => {
    fetchCompanies()
  }, [fetchCompanies])

  // Memoized event handlers with useCallback
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPagination(prev => ({ ...prev, page: 1 }))
  }, [])

  const clearFilters = useCallback(() => {
    setSearchInput('')
    setClientIdInput('')
    setFilters({
      search: '',
      company: '',
      clientId: '',
      dateFrom: '',
      dateTo: '',
      scoreMin: '',
      scoreMax: '',
      includeArchived: false
    })
    setPagination(prev => ({ ...prev, page: 1 }))
  }, [])

  const handleArchive = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir archiver cette évaluation ?')) {
      return
    }

    try {
      const token = localStorage.getItem('adminToken')
      const response = await fetch(assessmentApiUrl(id, 'archive'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        // Refresh the list
        fetchAssessments()
      } else {
        const data = await response.json()
        setError(data.error || 'Erreur lors de l\'archivage')
      }
    } catch (err) {
      setError('Erreur lors de l\'archivage de l\'évaluation')
    }
  }

  const handleUnarchive = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir désarchiver cette évaluation ?')) {
      return
    }

    try {
      const token = localStorage.getItem('adminToken')
      const response = await fetch(assessmentApiUrl(id, 'unarchive'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        // Refresh the list
        fetchAssessments()
      } else {
        const data = await response.json()
        setError(data.error || 'Erreur lors du désarchivage')
      }
    } catch (err) {
      setError('Erreur lors du désarchivage de l\'évaluation')
    }
  }

  const handlePageChange = useCallback((newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }))
  }, [])

  const handleSelectToggle = useCallback((id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(selectedId => selectedId !== id)
      } else {
        return [...prev, id]
      }
    })
  }, [])

  const handleSelectAll = useCallback((e) => {
    if (e.target.checked) {
      setSelectedIds(assessments.map(a => a.id))
    } else {
      setSelectedIds([])
    }
  }, [assessments])

  const handleCompareSelected = useCallback(() => {
    if (selectedIds.length >= 2 && selectedIds.length <= 5) {
      navigate(buildComparePath(selectedIds))
    }
  }, [selectedIds, navigate])

  // Memoized input handlers
  const handleSearchChange = useCallback((e) => {
    setSearchInput(e.target.value)
  }, [])

  const handleClientIdChange = useCallback((e) => {
    setClientIdInput(e.target.value)
  }, [])

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-600 bg-green-100'
    if (score >= 6) return 'text-yellow-600 bg-yellow-100'
    if (score >= 4) return 'text-orange-600 bg-orange-100'
    return 'text-red-600 bg-red-100'
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR')
  }

  if (loading && assessments.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Chargement des évaluations...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filtres et recherche</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recherche (nom, email, entreprise, N° Dossier)
            </label>
            <SearchInput
              value={searchInput}
              onChange={handleSearchChange}
              placeholder="Rechercher dans tous les champs..."
            />
          </div>

          {/* Company */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Entreprise
            </label>
            <select
              value={filters.company}
              onChange={(e) => handleFilterChange('company', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Toutes les entreprises</option>
              {companies.map(company => (
                <option key={company.name} value={company.name}>
                  {company.name} ({company.count}){company.clientId ? ` - ${company.clientId}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Client ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
            N° Dossier
            </label>
            <ClientIdInput
              value={clientIdInput}
              onChange={handleClientIdChange}
              placeholder="Ex: 12345 ou C12345"
            />
          </div>

          {/* Date From */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de début
            </label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de fin
            </label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Score Min */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Score minimum
            </label>
            <input
              type="number"
              min="0"
              max="10"
              step="0.1"
              value={filters.scoreMin}
              onChange={(e) => handleFilterChange('scoreMin', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="0.0"
            />
          </div>

          {/* Score Max */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Score maximum
            </label>
            <input
              type="number"
              min="0"
              max="10"
              step="0.1"
              value={filters.scoreMax}
              onChange={(e) => handleFilterChange('scoreMax', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="10.0"
            />
          </div>
        </div>

        {/* Archive Toggle */}
        <div className="mb-4 pt-4 border-t border-gray-200">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.includeArchived}
              onChange={(e) => handleFilterChange('includeArchived', e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">
              Afficher les évaluations archivées
            </span>
          </label>
        </div>

        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={clearFilters}
              className="text-gray-600 hover:text-gray-800 text-sm"
            >
              Effacer les filtres
            </button>
            {selectedIds.length > 0 && (
              <button
                onClick={() => setSelectedIds([])}
                className="text-gray-600 hover:text-gray-800 text-sm"
              >
                Désélectionner tout ({selectedIds.length})
              </button>
            )}
          </div>
          <div className="flex items-center space-x-4">
            {selectedIds.length >= 2 && selectedIds.length <= 5 && (
              <button
                onClick={handleCompareSelected}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Comparer les {selectedIds.length} sélectionnées
              </button>
            )}
            {selectedIds.length === 1 && (
              <span className="text-sm text-gray-500">
                Sélectionnez au moins 2 évaluations pour comparer (max 5)
              </span>
            )}
            {selectedIds.length > 5 && (
              <span className="text-sm text-orange-600">
                Sélectionnez entre 2 et 5 évaluations pour comparer
              </span>
            )}
            <p className="text-sm text-gray-600">
              {pagination.total} évaluation(s) trouvée(s)
            </p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Assessments Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={assessments.length > 0 && selectedIds.length === assessments.length}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Entreprise
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                N° Dossier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Consultant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Questions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date évaluation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date soumission
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 border-l border-gray-200 shadow-[inset_6px_0_12px_-6px_rgba(0,0,0,0.05),_-3px_0_6px_rgba(0,0,0,0.08)]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {assessments.map((assessment) => {
                const isArchived = assessment.isArchived === true
                return (
                <tr key={assessment.id} className={`group hover:bg-gray-50 ${isArchived ? 'opacity-75 bg-gray-50' : ''}`}>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(assessment.id)}
                      onChange={() => handleSelectToggle(assessment.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <div>
                        <p className={`text-sm font-medium ${isArchived ? 'text-gray-500' : 'text-gray-900'}`}>
                          {assessment.clientInfo.firstName} {assessment.clientInfo.lastName}
                        </p>
                        <p className="text-sm text-gray-500">
                          {assessment.clientInfo.email}
                        </p>
                      </div>
                      {isArchived && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
                          Archivé
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {assessment.clientInfo.company}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {assessment.clientInfo.clientId || <span className="text-gray-400">-</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {assessment.clientInfo.consultantEmail ? (
                      <a href={`mailto:${assessment.clientInfo.consultantEmail}`} className="text-blue-600 hover:text-blue-800">
                        {assessment.clientInfo.consultantEmail}
                      </a>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getScoreColor(assessment.scores.average)}`}>
                      {assessment.scores.average.toFixed(1)}/10
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {assessment.scores.completedQuestions}/{assessment.scores.totalQuestions}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(assessment.clientInfo.assessmentDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(assessment.storedAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {assessment.questionnaireVersion ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        v{assessment.questionnaireVersion}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500" title="Évaluation créée avant le système de versioning">
                        legacy
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 sticky right-0 bg-white group-hover:bg-gray-50 border-l border-gray-200 shadow-[inset_6px_0_12px_-6px_rgba(0,0,0,0.05),_-3px_0_6px_rgba(0,0,0,0.08)]">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => navigate(assessmentDetailPath(assessment.id))}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Voir détails
                      </button>
                      {isArchived ? (
                        <button
                          onClick={() => handleUnarchive(assessment.id)}
                          className="text-green-600 hover:text-green-800 font-medium flex items-center"
                          title="Désarchiver"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleArchive(assessment.id)}
                          className="text-gray-600 hover:text-gray-800 font-medium flex items-center"
                          title="Archiver"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Affichage <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> à{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span> sur{' '}
                  <span className="font-medium">{pagination.total}</span> résultats
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === pagination.page
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {assessments.length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucune évaluation trouvée
          </h3>
          <p className="text-gray-500">
            Aucune évaluation ne correspond aux critères de recherche.
          </p>
        </div>
      )}
    </div>
  )
}

export default AssessmentsList
